import {
  EXECUTION_BLUEPRINT_LIVE_REVIEW,
  EXECUTION_BLUEPRINT_WIG_CONNECTIONS,
  EXECUTION_BLUEPRINT_WIG_CONNECTIONS_LOADING,
  EXECUTION_BLUEPRINT_YEAR_ONE_PLAN,
} from "../../../../data/RouteUrls";
import { LIVE_REVIEW_STEP } from "../ExecutionBlueprintLiveReview/_socket/LiveReviewSocketStore";

// ═════════════════════════════════════════════════════════════════════════════
// `live_review_step` → the screen that owns it. One table, one place.
//
// ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
// Until now this mapping was written out by hand in FOUR screens — the Live
// Review screen, the drafting wait, WIG Connections and Year 1 Plan — each as
// its own if/else ladder, and each covering only the steps it happened to care
// about. Four copies of one table is four chances for them to disagree, and a
// disagreement here doesn't look like a bug: it looks like the two people in a
// live session ending up on different screens.
//
// It also became the ENTRY point mapping on 2026-08-31, when `live_review_step`
// started arriving on the coach dashboard payload. Before that, the only way to
// find out which stage a session had reached was to open the Live Review screen,
// join the socket and let its step-routing bounce you onward — so a coach
// resuming a session at the Year 1 stage would watch two screens flash past on
// the way. The dashboard can now send them straight there.
//
// ⚠️ AN UNKNOWN STEP RETURNS NULL, and callers must treat that as "stay put"
// rather than falling back to a guess. §9 of `12-live-review-fe-handoff.md` is
// explicit that an unrecognised step is a reason to stop rather than to guess,
// and the failure mode of guessing is a redirect loop between two screens that
// each think the other owns the step.
// ═════════════════════════════════════════════════════════════════════════════

export const LIVE_REVIEW_STEP_ROUTES = {
  [LIVE_REVIEW_STEP.FINALIZE_BLUEPRINT]: EXECUTION_BLUEPRINT_LIVE_REVIEW,
  [LIVE_REVIEW_STEP.CONNECTORS_DRAFTING]:
    EXECUTION_BLUEPRINT_WIG_CONNECTIONS_LOADING,
  [LIVE_REVIEW_STEP.CONNECTORS_REVIEW]: EXECUTION_BLUEPRINT_WIG_CONNECTIONS,
  // ⚠️ `year1_drafting` goes to the Year 1 PLAN route, not to
  // `EXECUTION_BLUEPRINT_YEAR_ONE_PLAN_DRAFTING`. That separate wait screen was
  // dropped on 2026-08-31 and the Year 1 screen owns its own loading state now.
  // The route constant still exists and is still registered (pointing at the
  // connectors wait), which is why this line is called out rather than left to
  // look like a typo — see the note at the bottom of this file.
  [LIVE_REVIEW_STEP.YEAR1_DRAFTING]: EXECUTION_BLUEPRINT_YEAR_ONE_PLAN,
  [LIVE_REVIEW_STEP.YEAR1_REVIEW]: EXECUTION_BLUEPRINT_YEAR_ONE_PLAN,
};

/**
 * The route for a step, with `:blueprintId` already filled in.
 *
 * @param step        A `live_review_step` value, from the socket snapshot or
 *                    from the dashboard payload. Null/undefined is fine.
 * @param blueprintId The blueprint's `unique_id`.
 * @returns The path, or `null` when the step is missing or unrecognised.
 */
export const routeForLiveReviewStep = (step, blueprintId) => {
  const route = LIVE_REVIEW_STEP_ROUTES[step];
  if (!route || !blueprintId) return null;
  return route.replace(":blueprintId", blueprintId);
};

/**
 * Step-routing for a screen that owns one or more steps.
 *
 * Returns the path this screen should send the user to, or `null` to stay put.
 * "Stay put" covers three separate cases that all mean the same thing to a
 * caller and are worth keeping distinct in the reading:
 *
 *   - no step yet (the snapshot hasn't landed) — never redirect on absence, or
 *     every one of these screens bounces on mount before it knows anything
 *   - the step is one of ours
 *   - the step is unrecognised (see the header)
 *
 * @param step        The current `live_review_step`.
 * @param ownSteps    The steps this screen is the right home for.
 * @param blueprintId The blueprint's `unique_id`.
 */
export const redirectForStep = (step, ownSteps, blueprintId) => {
  if (!step || ownSteps.includes(step)) return null;
  return routeForLiveReviewStep(step, blueprintId);
};

export default LIVE_REVIEW_STEP_ROUTES;
